import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AlertTriangle, Check, X, Ban, MessageSquare, User, Clock } from 'lucide-react';

const STATUS_COLORS = { pending: 'bg-amber-100 text-amber-800', resolved: 'bg-green-100 text-green-800', dismissed: 'bg-slate-100 text-slate-600' };

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/reports').then(({ data }) => { setReports(data.reports || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateStatus = async (reportId, status) => {
    try {
      await api.put(`/admin/reports/${reportId}`, { status });
      setReports(prev => prev.map(r => r.report_id === reportId ? {...r, status} : r));
      toast.success(`Report ${status}`);
    } catch { toast.error('Failed to update report'); }
  };

  const banUser = async (userId, reportId) => {
    try {
      await api.post(`/admin/users/${userId}/ban`);
      setReports(prev => prev.map(r => r.report_id === reportId ? {...r, status: 'resolved', reported_user_banned: true} : r));
      toast.success('User banned');
    } catch { toast.error('Failed to ban user'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#df5681] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-reports-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Player Reports</h1>
      <p className="text-slate-500 mb-8">Review and take action on player reports.</p>

      {/* Action Legend */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Action Buttons Guide</p>
          <div className="flex flex-wrap gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center"><Check className="w-4 h-4 text-green-600" /></div>
              <span><strong>Resolve</strong> — Mark as reviewed and handled</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center"><X className="w-4 h-4 text-slate-500" /></div>
              <span><strong>Dismiss</strong> — Not actionable, close the report</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center"><Ban className="w-4 h-4 text-red-600" /></div>
              <span><strong>Ban User</strong> — Ban the reported user permanently</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {reports.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No reports yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map(r => (
            <Card key={r.report_id} className="border-slate-100" data-testid={`report-${r.report_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs rounded-full ${STATUS_COLORS[r.status] || STATUS_COLORS.pending}`}>{r.status || 'pending'}</Badge>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                    </div>

                    {/* Reporter Info */}
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Reported by: <strong>{r.reporter_name}</strong></span>
                      {r.reporter_email && <span className="text-xs text-slate-400">({r.reporter_email})</span>}
                    </div>

                    {/* Reported User */}
                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-[#df5681]" />
                      <span className="text-slate-600">Against: <strong className="text-[#df5681]">{r.reported_user_name}</strong></span>
                      {r.reported_user_email && <span className="text-xs text-slate-400">({r.reported_user_email})</span>}
                      {r.reported_user_banned && <Badge className="text-xs bg-red-100 text-red-700 rounded-full">Banned</Badge>}
                    </div>

                    {/* Reason */}
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <p className="text-xs font-medium text-slate-500 mb-1">Reason</p>
                      <p className="text-sm text-slate-700">{r.reason}</p>
                    </div>

                    {/* Reported Message Content */}
                    {r.reported_message_content && (
                      <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                        <p className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Reported Message</p>
                        <p className="text-sm text-slate-700 italic">"{r.reported_message_content}"</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {r.status === 'pending' && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button size="sm" onClick={() => updateStatus(r.report_id, 'resolved')} className="rounded-lg bg-green-100 text-green-700 hover:bg-green-200 gap-1" title="Resolve — Mark as reviewed and handled" data-testid={`resolve-${r.report_id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => updateStatus(r.report_id, 'dismissed')} className="rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 gap-1" title="Dismiss — Not actionable, close report" data-testid={`dismiss-${r.report_id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => banUser(r.reported_user_id, r.report_id)} className="rounded-lg bg-red-100 text-red-600 hover:bg-red-200 gap-1" title="Ban User — Ban reported user permanently" data-testid={`ban-${r.report_id}`}>
                        <Ban className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
